import React, { useState, useEffect } from 'react';
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

const TestReviewPage = () => {
  const { state } = useLocation();
  const { quizId: paramQuizId } = useParams();  // supports /test-review/:quizId from history
  const navigate = useNavigate();

  const [questions, setQuestions]     = useState([]);
  const [quiz, setQuiz]              = useState(state?.quiz || null);
  const [userAnswers, setUserAnswers] = useState(state?.userAnswers || {});
  const [loading, setLoading]         = useState(true);
  const [currentIdx, setCurrentIdx]   = useState(0);
  const [viewMode, setViewMode]       = useState('one-by-one');
  const [filter, setFilter]           = useState('all');

  // Determine quizId — from router state or URL param
  const resolvedQuizId = quiz?.QuizId || paramQuizId;

  useEffect(() => {
    if (!resolvedQuizId) {
      setLoading(false);
      return;
    }
    api.get(`/test-zone/review/${resolvedQuizId}`)
      .then(res => {
        if (res.data.success) {
          setQuestions(res.data.questions);
          // If opened from history (no state), populate quiz from API response
          if (!quiz && res.data.quiz) {
            setQuiz(res.data.quiz);
          }
        }
      })
      .catch(() => alert('Failed to load review data'))
      .finally(() => setLoading(false));
  }, [resolvedQuizId]);

  // No quiz data at all
  if (!loading && !resolvedQuizId) {
    return (
      <div className="min-h-screen bg-[#1a0b2e] flex items-center justify-center p-4">
        <div className="text-white text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <p className="text-xl mb-4">No review data found.</p>
          <Link to="/test-zone" className="px-6 py-3 bg-purple-600 rounded-xl font-bold">
            Back to Test Zone
          </Link>
        </div>
      </div>
    );
  }

  // ── Enrich questions with answer status ─────────────────────────────────
  const enriched = questions.map(q => {
    const userOptId   = userAnswers[q.QuesId];
    const correctOpts = q.options.filter(o => o.IsCorrect == 1);
    const userOpt     = q.options.find(o => o.OptionId === userOptId);
    const isCorrect   = userOpt?.IsCorrect == 1;
    const isSkipped   = userOptId === undefined;

    return { ...q, userOptId, correctOpts, userOpt, isCorrect, isSkipped };
  });

  // ── Filtered list ────────────────────────────────────────────────────────
  const filtered = enriched.filter(q => {
    if (filter === 'correct') return q.isCorrect;
    if (filter === 'wrong')   return !q.isCorrect && !q.isSkipped;
    if (filter === 'skipped') return q.isSkipped;
    return true;
  });

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = {
    correct:  enriched.filter(q => q.isCorrect).length,
    wrong:    enriched.filter(q => !q.isCorrect && !q.isSkipped).length,
    skipped:  enriched.filter(q => q.isSkipped).length,
    total:    enriched.length,
  };

  // ── Navigate filtered list ───────────────────────────────────────────────
  const safeCurrent = Math.min(currentIdx, Math.max(0, filtered.length - 1));
  const currentQ    = filtered[safeCurrent];

  const goTo = (idx) => {
    setCurrentIdx(Math.max(0, Math.min(idx, filtered.length - 1)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return (
    <div className="min-h-screen bg-[#1a0b2e] flex items-center justify-center text-white text-lg">
      Loading Review...
    </div>
  );

  // ─── Shared question card ─────────────────────────────────────────────────
  const QuestionCard = ({ q, index, totalShown }) => (
    <div className={`bg-white rounded-2xl shadow-md overflow-hidden mb-6 border-l-4 ${
      q.isSkipped ? 'border-gray-300' : q.isCorrect ? 'border-green-500' : 'border-red-500'
    }`}>
      {/* Card Header */}
      <div className={`px-5 py-3 flex items-center justify-between text-sm font-semibold ${
        q.isSkipped
          ? 'bg-gray-50 text-gray-500'
          : q.isCorrect
          ? 'bg-green-50 text-green-700'
          : 'bg-red-50 text-red-700'
      }`}>
        <span>
          Question {enriched.findIndex(e => e.QuesId === q.QuesId) + 1}
          {totalShown > 1 && ` (${index + 1} of ${totalShown} shown)`}
        </span>
        <span className="flex items-center gap-1.5">
          {q.isSkipped ? (
            <><span className="w-2 h-2 rounded-full bg-gray-400 inline-block"></span>Skipped</>
          ) : q.isCorrect ? (
            <><span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>Correct · +{q.Marks} mark{q.Marks != 1 ? 's' : ''}</>
          ) : (
            <><span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>Wrong</>
          )}
        </span>
      </div>

      <div className="p-5 sm:p-6">
        {/* Question Text */}
        <div
          className="text-base sm:text-lg text-gray-900 leading-relaxed mb-6"
          dangerouslySetInnerHTML={{ __html: q.Question }}
        />

        {/* Options */}
        <div className="space-y-3">
          {q.options.map(opt => {
            const isUserChoice = opt.OptionId === q.userOptId;
            const isCorrectOpt = opt.IsCorrect == 1;

            let cls = 'border-gray-200 bg-gray-50 text-gray-700';
            let icon = null;

            if (isCorrectOpt && isUserChoice) {
              cls = 'border-green-500 bg-green-50 text-green-900';
              icon = <span className="ml-auto text-green-600 font-bold text-lg flex-shrink-0">✓</span>;
            } else if (isCorrectOpt) {
              cls = 'border-green-400 bg-green-50/60 text-green-800';
              icon = <span className="ml-auto text-green-500 font-bold flex-shrink-0">✓</span>;
            } else if (isUserChoice) {
              cls = 'border-red-400 bg-red-50 text-red-800';
              icon = <span className="ml-auto text-red-500 font-bold flex-shrink-0">✗</span>;
            }

            return (
              <div
                key={opt.OptionId}
                className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl border-2 transition-all ${cls}`}
              >
                {/* Radio indicator */}
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  isCorrectOpt
                    ? 'border-green-500 bg-green-500'
                    : isUserChoice
                    ? 'border-red-400 bg-red-400'
                    : 'border-gray-300 bg-white'
                }`}>
                  {(isCorrectOpt || isUserChoice) && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>

                <div
                  className="text-sm sm:text-base font-medium flex-1"
                  dangerouslySetInnerHTML={{ __html: opt.OptionText }}
                />

                {icon}

                {/* Labels */}
                <div className="flex gap-1 flex-shrink-0">
                  {isUserChoice && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">
                      Your answer
                    </span>
                  )}
                  {isCorrectOpt && !isUserChoice && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">
                      Correct answer
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Skipped notice */}
        {q.isSkipped && (
          <div className="mt-4 p-3 bg-gray-100 rounded-xl text-sm text-gray-500 flex items-center gap-2">
            <span>⏭️</span> You skipped this question.
            {q.correctOpts.length > 0 && (
              <span> The correct answer is highlighted above.</span>
            )}
          </div>
        )}

        {/* Explanation */}
        {q.explanation && (
          <div className="mt-5 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-xl">
            <div className="flex items-center gap-2 text-blue-800 font-bold text-sm uppercase tracking-wide mb-2">
              <span>💡</span> Explanation
            </div>
            <div
              className="text-sm text-gray-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: q.explanation }}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen mt-20 bg-[#0f0720] text-white">

      {/* ── Top Bar ─────────────────────────────────────────────────────── */}
      <div className="bg-[#1a0b2e] border-b border-white/10 px-4 py-3 flex items-center justify-between gap-3 sticky top-0 z-20">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-300 hover:text-white flex items-center gap-1 text-sm flex-shrink-0"
        >
          ← Back
        </button>
        <div className="text-center min-w-0 flex-1">
          <p className="font-bold text-sm sm:text-base truncate">{quiz?.Title || quiz?.title || 'Test Review'}</p>
          <p className="text-xs text-gray-400">Review</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => { setViewMode('one-by-one'); setCurrentIdx(0); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              viewMode === 'one-by-one' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300'
            }`}
          >
            One by one
          </button>
          <button
            onClick={() => setViewMode('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              viewMode === 'all' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300'
            }`}
          >
            All
          </button>
        </div>
      </div>

      {/* ── Summary Strip ────────────────────────────────────────────────── */}
      <div className="bg-[#1a0b2e]/80 px-4 py-3 flex items-center justify-center gap-3 sm:gap-6 text-sm flex-wrap border-b border-white/10">
        {[
          { label: 'Total', count: stats.total,   color: 'text-white',        key: 'all'     },
          { label: '✓ Correct', count: stats.correct, color: 'text-green-400',  key: 'correct' },
          { label: '✗ Wrong',   count: stats.wrong,   color: 'text-red-400',    key: 'wrong'   },
          { label: '— Skipped', count: stats.skipped, color: 'text-gray-400',   key: 'skipped' },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => { setFilter(s.key); setCurrentIdx(0); }}
            className={`flex items-center gap-1.5 font-semibold transition-all px-3 py-1 rounded-lg ${
              filter === s.key ? 'bg-white/15 ring-1 ring-white/30' : 'hover:bg-white/10'
            } ${s.color}`}
          >
            <span className="text-lg">{s.count}</span>
            <span className="text-xs opacity-80">{s.label}</span>
          </button>
        ))}
      </div>

      {/* ── Main Content ─────────────────────────────────────────────────── */}
      <div className="max-w-3xl mx-auto px-4 py-6">

        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-4xl mb-3">🔍</div>
            <p>No questions in this category.</p>
            <button onClick={() => setFilter('all')} className="mt-4 text-purple-400 underline text-sm">
              Show all questions
            </button>
          </div>
        ) : viewMode === 'all' ? (
          // ── All view ────────────────────────────────────────────────────
          filtered.map((q, i) => (
            <QuestionCard key={q.QuesId} q={q} index={i} totalShown={filtered.length} />
          ))
        ) : (
          // ── One-by-one view ─────────────────────────────────────────────
          <>
            <QuestionCard q={currentQ} index={safeCurrent} totalShown={filtered.length} />

            {/* Navigation */}
            <div className="flex items-center justify-between gap-3 mt-2">
              <button
                onClick={() => goTo(safeCurrent - 1)}
                disabled={safeCurrent === 0}
                className="px-5 py-2.5 bg-white/10 hover:bg-white/20 rounded-xl font-semibold text-sm disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                ← Previous
              </button>

              <span className="text-gray-400 text-sm">
                {safeCurrent + 1} / {filtered.length}
              </span>

              <button
                onClick={() => goTo(safeCurrent + 1)}
                disabled={safeCurrent === filtered.length - 1}
                className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 rounded-xl font-semibold text-sm disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next →
              </button>
            </div>

            {/* Mini palette */}
            <div className="mt-8 bg-white/5 rounded-2xl p-4">
              <p className="text-xs text-gray-400 mb-3 font-semibold uppercase tracking-wider">
                Jump to question
              </p>
              <div className="flex flex-wrap gap-2">
                {filtered.map((q, i) => (
                  <button
                    key={q.QuesId}
                    onClick={() => goTo(i)}
                    className={`w-9 h-9 rounded-lg text-xs font-bold transition-all ${
                      i === safeCurrent
                        ? 'ring-2 ring-white scale-110'
                        : ''
                    } ${
                      q.isSkipped
                        ? 'bg-gray-600 text-gray-300'
                        : q.isCorrect
                        ? 'bg-green-600 text-white'
                        : 'bg-red-600 text-white'
                    }`}
                    title={`Q${enriched.findIndex(e => e.QuesId === q.QuesId) + 1} — ${
                      q.isSkipped ? 'Skipped' : q.isCorrect ? 'Correct' : 'Wrong'
                    }`}
                  >
                    {enriched.findIndex(e => e.QuesId === q.QuesId) + 1}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Done button */}
        <div className="mt-8 text-center">
          <Link
            to="/test-zone"
            className="inline-flex items-center gap-2 px-8 py-3 bg-purple-600 hover:bg-purple-700 rounded-xl font-bold transition-colors"
          >
            ← Back to Test Zone
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TestReviewPage;